// Выбор человека для расчёта. Это и есть вход во вкладку: нажал на человека, и документ строится
// сразу, отдельной кнопки «Рассчитать» нет.
//
// ## Что видно и когда
//
// Сразу: подсказка, что делать, и поле поиска. Пока едет список, под полем скелет, а не пустота.
// Поиск пуст: уволенные, потому что расчёт нужен прежде всего тому, кто ушёл или уходит. Есть
// запрос: все состояния, работающий тоже, потому что считать могут и перед увольнением. Нет
// связи: причина словами и «Повторить». Никого не нашли: что проверить, а не белое поле.
//
// Если человека уже открывали в этом сеансе, над поиском стоит строка «Вернуться к расчёту»: из
// документа уходят создавать ведомость или открывать табель, а кнопки «назад» в программе нет.

import { useState } from 'react';

import { Link } from 'react-router';

import { RiArrowRightLine, RiSearchLine, RiUserSearchLine } from '@remixicon/react';

import { formatDateOnly } from '@/shared/time/format';
import { адресРасчётаСотрудника } from '@/shared/payroll/расчёт-сотрудника';
import { countLabel } from '@/shared/text/plural';
import * as Badge from '@/shared/ui/badge';
import * as LinkButton from '@/shared/ui/link-button';
import { Block } from '@/shared/ui/block';
import { ListRow, ListSkeleton } from '@/shared/ui/list-row';
import { EmptyState, ScreenState } from '@/shared/ui/screen-state';
import { TextField } from '@/shared/ui/text-field';

import { useЛюдиДляРасчёта, useЧеловек } from './queries';
import { подписьЧеловека } from './тексты';

const ЛЮДИ = ['человек', 'человека', 'человек'] as const;

export function ВыборЧеловека({ последний }: { последний: string | null }) {
  const [запрос, setЗапрос] = useState('');
  const люди = useЛюдиДляРасчёта(запрос);
  const прежний = useЧеловек(последний);
  const ищем = запрос.trim() !== '';

  return (
    <div className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5 узко:p-4'>
      <div className='flex max-w-xl flex-col gap-1'>
        <h2 className='text-label-md text-text-strong-950'>Кого рассчитываем</h2>
        <p className='text-paragraph-sm text-text-sub-600'>
          Выберите человека: расчёт за все его месяцы и объекты появится сразу, отдельной кнопки не
          нужно.
        </p>
      </div>

      {прежний.data !== undefined && последний !== null && (
        <div className='max-w-xl'>
          <LinkButton.Root variant='primary' underline asChild>
            <Link to={адресРасчётаСотрудника(последний)} className='цель-тача relative'>
              Вернуться к расчёту: {прежний.data.display_name}
              <LinkButton.Icon as={RiArrowRightLine} />
            </Link>
          </LinkButton.Root>
        </div>
      )}

      <div className='max-w-xl'>
        <TextField
          id='settlement-search'
          label='Найти человека'
          icon={RiSearchLine}
          value={запрос}
          onChange={setЗапрос}
          placeholder='Фамилия, имя или телефон'
          autoComplete='off'
          hint={
            ищем
              ? 'Ищем среди всех: уволенные стоят первыми.'
              : 'Пока поиск пуст, показаны уволенные.'
          }
        />
      </div>

      <Block
        className='max-w-xl'
        flush
        title={ищем ? 'Найдено' : 'Уволенные'}
        note={люди.data === undefined ? undefined : countLabel(люди.data.items.length, ЛЮДИ)}
      >
        <ScreenState
          data={люди.data}
          error={люди.error}
          subject='список людей'
          isEmpty={(данные) => данные.items.length === 0}
          onRetry={() => void люди.refetch()}
          loading={<ListSkeleton rows={5} blockInset />}
          empty={
            <EmptyState
              icon={RiUserSearchLine}
              title={ищем ? 'Никого не нашли' : 'Уволенных пока нет'}
              description={
                ищем
                  ? 'Проверьте фамилию или поищите по телефону или табельному номеру.'
                  : 'Начните вводить фамилию: расчёт можно построить и для работающего, например перед увольнением.'
              }
            />
          }
        >
          {(данные) => (
            <ul>
              {данные.items.map((человек) => (
                <li key={человек.id}>
                  <ListRow
                    blockInset
                    to={адресРасчётаСотрудника(человек.id)}
                    selected={false}
                    title={человек.display_name}
                    subtitle={подписьЧеловека(человек) || 'на объекте не числится'}
                    // Даты увольнения может не быть (состояние ставят и пачкой из реестра, а там
                    // её не спрашивают): плашка «Уволен —» с прочерком читалась как поломка.
                    // Нет даты, нет плашки.
                    badge={
                      человек.status === 'fired' && человек.fired_at ? (
                        <Badge.Root variant='lighter' size='medium' color='gray'>
                          Уволен {formatDateOnly(человек.fired_at)}
                        </Badge.Root>
                      ) : undefined
                    }
                  />
                </li>
              ))}
            </ul>
          )}
        </ScreenState>
      </Block>
    </div>
  );
}
